import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Button,
  CssBaseline,
  Grid,
  Backdrop,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import Header from "../../../../common/Header";
import Sidebar from "../../../../common/Sidebar";
import GeneralAdminSidebar from "../../../../common/GeneralAdminSidebar";
import TableHeadComponent from "../../../../common/TableHeadComponent";
import TablePaginationSection from "../../../../common/TablePaginationSection";
import { FilterSection, SearchBar } from "../../components/components";
import { TableRowSection } from "../../components/TableRowSection/TableRowSection";
import FilterModalComponent from "../../../../common/FilterModalComponent";
import QCTemplatesPanel from "../../../OrganizationQCTemplates/components/QCTemplatesPanel/QCTemplatesPanel";

import {
  EmailTemplatesTableHeadCells,
  getComparator,
  stableSort,
  routes,
  GeneralAdminRoutes,
  mainLayoutStyles,
  showNotification,
} from "../../../../constants";
import { useAuth } from "../../../../hooks";
import {
  EMAIL_TEMPLATE_SCOPE_GEN,
  EMAIL_TEMPLATE_SCOPE_ORG,
  useGetEmailTemplates,
  useBulkUpdateTemplateStatus,
  useDuplicateTemplates,
} from "../../hooks/EmailTemplatesHooks";

/**
 * Email templates list page.
 *  - scope "org" (default): the organization's templates (orgEmailTemplates)
 *  - scope "gen": General Admin standard templates (genEmailTemplates)
 */
const EmailTemplatesContainer = ({ scope = EMAIL_TEMPLATE_SCOPE_ORG }) => {
  const navigate = useNavigate();
  const { currentUser, organizationId: authOrganizationId } = useAuth();
  const isGen = scope === EMAIL_TEMPLATE_SCOPE_GEN;
  const organizationId = isGen ? null : authOrganizationId;
  
  const userId = currentUser?.id;

  const { templates, loading, refetch } = useGetEmailTemplates(organizationId, {
    scope,
  });
  const { bulkUpdateStatus, loading: bulkStatusLoading } = useBulkUpdateTemplateStatus(scope);
  const { duplicateTemplates, loading: duplicateLoading } = useDuplicateTemplates(scope);

  // Standard templates have no "default" flag, so hide that column
  const tableHeadCells = useMemo(
    () =>
      isGen
        ? EmailTemplatesTableHeadCells.filter((c) => c.id !== "default")
        : EmailTemplatesTableHeadCells,
    [isGen]
  );

  // Tabs: 0 = Emails, 1 = QuickConnect.
  // See docs/QUICKCOLLECT_IMPLEMENTATION.md
  const [activeTab, setActiveTab] = useState(0);

  // Table state
  const [order, setOrder] = useState("desc");
  // Default sort: Status (desc) then Updated At (desc)
  // We still keep MUI's single-column sort UI, but default the initial ordering.
  const [orderBy, setOrderBy] = useState("status");
  const [selected, setSelected] = useState([]);
  // TablePaginationSection expects a 1-based page index (same pattern as ReactionsTable)
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  // Filters (QuickTales-style)
  const [filterModal, setFilterModal] = useState(false);
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);

  const handleFilterModalOpen = () => setFilterModal(true);
  const handleFilterModalClose = () => setFilterModal(false);

  const handleDeleteChip = async (filterType, filterItem) => {
    if (filterType === "status") {
      setStatusFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    }
  };

  const resetFilter = async () => {
    setStatusFilterOptions([]);
    setSelected([]);
    setPage(1);
    handleFilterModalClose();
  };

  // When filters change, reset to page 1 and clear selection
  useEffect(() => {
    setSelected([]);
    setPage(1);
  }, [statusFilterOptions]);

  // Filter templates based on search + status filter
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Status filter logic
    const normalizedStatusOptions = (statusFilterOptions || []).map((s) =>
      String(s).toLowerCase()
    );
    const hasAll = normalizedStatusOptions.includes("all");
    const selectedStatuses = normalizedStatusOptions.filter(
      (s) => s === "active" || s === "inactive"
    );

    if (!hasAll && selectedStatuses.length > 0) {
      filtered = filtered.filter((t) =>
        selectedStatuses.includes(String(t.status || "").toLowerCase())
      );
    }

    // Search filter logic
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.name?.toLowerCase().includes(lowerSearch) ||
          template.description?.toLowerCase().includes(lowerSearch)
      );
    }

    return filtered;
  }, [templates, searchText, statusFilterOptions]);

  const statusRank = (status) => {
    const s = String(status || "").toLowerCase();
    // Desc sort should put Active first, then Inactive.
    if (s === "active") return 2;
    if (s === "inactive") return 1;
    return 0;
  };

  const defaultStatusThenUpdatedAtComparator = (a, b) => {
    const statusDiff = statusRank(b.status) - statusRank(a.status);
    if (statusDiff !== 0) return statusDiff;

    const aUpdated = a.updatedAt?.seconds ?? -Infinity;
    const bUpdated = b.updatedAt?.seconds ?? -Infinity;
    return bUpdated - aUpdated;
  };

  // Sort and paginate
  const sortedTemplates = useMemo(() => {
    // If user is sorting by a column explicitly (anything other than the default status)
    // use the standard comparator.
    if (orderBy !== "status") {
      return stableSort(filteredTemplates, getComparator(order, orderBy));
    }

    // Otherwise apply default two-level sort.
    // When order is "asc" on status, fall back to standard comparator.
    if (order !== "desc") {
      return stableSort(filteredTemplates, getComparator(order, orderBy));
    }

    return stableSort(filteredTemplates, defaultStatusThenUpdatedAtComparator);
  }, [filteredTemplates, order, orderBy]);

  const paginatedTemplates = useMemo(
    () =>
      sortedTemplates.slice(
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      ),
    [sortedTemplates, page, rowsPerPage]
  );

  // Handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = paginatedTemplates.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleRowClick = (templateId) => {
    navigate(
      isGen
        ? `/standard-email-templates/details/${templateId}`
        : `/email-templates/details/${templateId}`
    );
  };

  const handleCreateTemplate = () => {
    navigate(isGen ? GeneralAdminRoutes.createGenEmailTemplate : routes.createEmailTemplate);
  };

  const handleAddStandardTemplate = () => {
    navigate(routes.standardEmailTemplates);
  };

  const handleSearch = (text) => {
    setSearchText(text);
    setPage(1);
  };

  // Bulk actions
  const handleBulkStatusChange = async (newStatus) => {
    if (selected.length === 0) {
      showNotification("Please select at least one template");
      return;
    }

    const result = await bulkUpdateStatus(selected, newStatus, userId);
    if (result.success) {
      showNotification(`Successfully updated ${selected.length} template(s) to ${newStatus}`);
      setSelected([]);
      refetch();
    } else {
      showNotification("Error updating templates");
    }
  };

  const handleDuplicate = async () => {
    if (selected.length === 0) {
      showNotification("Please select at least one template to duplicate");
      return;
    }

    const result = await duplicateTemplates(selected, userId, organizationId);
    if (result.success) {
      showNotification(`Successfully duplicated ${selected.length} template(s)`);
      setSelected([]);
      refetch();
    } else {
      showNotification("Error duplicating templates");
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <Header
        mainTitle={isGen ? "Standard Email Templates" : "Email Templates"}
        bgColor="#fff"
      />
      {isGen ? <GeneralAdminSidebar /> : <Sidebar />}
      {activeTab === 0 && !isGen && (
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddStandardTemplate}
        sx={{
          position: "absolute",
          top: 20,
          right: 240,
          borderColor: "#00bcd4",
          color: "#00bcd4",
          backgroundColor: "#fff",
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 600,
          padding: "9px 24px",
          "&:hover": {
            borderColor: "#00acc1",
            backgroundColor: "#f5fdfe",
          },
          zIndex: 3000,
        }}
      >
        Add Standard Template
      </Button>
      )}
      {activeTab === 0 && (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleCreateTemplate}
        sx={{
          position: "absolute",
          top: 20,
          right: 40,
          backgroundColor: "#00bcd4",
          color: "#fff",
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 600,
          padding: "10px 24px",
          "&:hover": {
            backgroundColor: "#00acc1",
          },
          zIndex: 3000,
        }}
      >
        Create Template
      </Button>
      )}
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>

          {!isGen && (

          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: "1px solid #e0e0e0",
              mb: 2,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            }}
          >
            <Tab label="Emails" />
            <Tab label="QuickConnect" />
          </Tabs>
          )}
          {isGen && <Box sx={{ height: 40 }} />}


          {/* Tab 0 — Emails */}
          <Box hidden={activeTab !== 0}>
          <FilterSection
            handleFilterModalOpen={handleFilterModalOpen}
            statusFilterOptions={statusFilterOptions}
            handleDeleteChip={handleDeleteChip}
          />
          <SearchBar
            searchText={searchText}
            onSearch={handleSearch}
            selectedCount={selected.length}
            onBulkStatusChange={handleBulkStatusChange}
            onDuplicate={handleDuplicate}
            bulkLoading={bulkStatusLoading || duplicateLoading}
          />

          {/* Match QuickTalesContainer spacing between search row and table */}
          <Box sx={{ height: 6 }} />

          {/* Table */}
          <Paper sx={{ width: "100%", mb: 2, mx: 2 }}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
                <TableHeadComponent
                  TableHeadData={tableHeadCells}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={paginatedTemplates.length}
                />
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{
                          py: 5,
                          fontFamily: "Avenir Book, sans-serif",
                          color: "#595959",
                        }}
                      >
                        {searchText
                          ? "No templates found matching your search"
                          : "No email templates yet. Click 'Create Template' to add one."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTemplates.map((row, index) => {
                      const isItemSelected = isSelected(row.id);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRowSection
                          key={row.id}
                          row={row}
                          isItemSelected={isItemSelected}
                          labelId={labelId}
                          handleClick={handleClick}
                          handleRowClick={handleRowClick}
                          showDefault={!isGen}
                        />
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePaginationSection
              // TablePaginationSection expects `count` to be number of pages
              count={Math.ceil(filteredTemplates.length / rowsPerPage)}
              rowsPerPage={rowsPerPage}
              page={page}
              total={filteredTemplates.length ?? 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          </Box>

          {/* Tab 1 - QuickConnect */}
          {activeTab === 1 && !isGen && <QCTemplatesPanel />}
        </Grid>
      </Grid>

      <FilterModalComponent
        modalOpen={filterModal}
        handleClose={handleFilterModalClose}
        resetFilter={resetFilter}
        // Filtering is done client-side, so handleFilter just closes modal.
        handleFilter={async () => {
          setSelected([]);
          setPage(1);
          handleFilterModalClose();
        }}
        data={[
          {
            mainOptions: statusFilterOptions,
            setMainOptions: setStatusFilterOptions,
            type: "text",
            title: "Status",
            currentOptions: [
              { label: "All", selected: true },
              { label: "active", selected: false },
              { label: "inactive", selected: false },
            ],
          },
        ]}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default EmailTemplatesContainer;
